import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useLoginInfo from 'hooks/useLoginInfo';
import { useHistory } from 'react-router-dom';
import { ReactComponent as PayDay } from "assets/images/icons/project-icons/pay_day.svg"; 
import { ReactComponent as PayrollDocument } from "assets/images/icons/project-icons/payroll_document.svg"; 
import { ReactComponent as LineChart } from "assets/images/icons/project-icons/line_chart.svg"; 
import Maybe from 'components/__new/common/Maybe';
import { resetEmployerProductsAction, updateEmployerProducts } from 'store/auth/account/action';
import Spinner from 'components/__new/common/Spinner';

const WelcomeScreen = (props) => {
    const { step, image, title, description, submitText, handleNext, loading } = props;
    
    return (
        <div className="mx-auto p-8 mt-7 max-w-xl text-center rounded-3xl bg-white">
            <Maybe condition={!loading}>
                <div className="h-32">
                    {image}
                </div>
                <div className="mt-8 font-semibold text-2xl leading-7">
                    {title}
                </div>
                <div className="mt-5 text-[#4A5564] leading-6 text-lg tracking-wide">
                    {description}
                </div>
                <div className="my-10 flex space-x-1.5 items-center justify-center">
                    {[...Array(3).keys()].map((e) => (
                        <React.Fragment key={e}>
                            <Maybe condition={(e+1) === step}>
                                <div className="w-3 h-3 bg-ep-primary-600 rounded-full"></div>
                            </Maybe>
                            <Maybe condition={(e+1) !== step}>
                                <div className="w-2 h-2 bg-[#9CA3AF] rounded-full"></div>
                            </Maybe>
                        </React.Fragment>
                    ))}
                </div>
                <div className="">
                    <button type="button" onClick={handleNext} className="btn btn-block btn-lg btn-ep-primary">
                        {submitText}
                    </button>
                </div>
            </Maybe>
            <Maybe condition={loading}>
                <Spinner message="Finishing Onboarding..." />
            </Maybe>
        </div>
    )
}

export const PayrollOnboarding = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { userInfo, updateEmployerProductsState } = useLoginInfo();
    const { employerProducts, employerProductsLoading } = useSelector((s) => s.auth.account);
    const [step, setStep] = useState(1);
    
    const handleCompletePayrollOnboarding = () => {
        const employerId = userInfo?.employer?._id;
        const employerProducts = userInfo?.employer?.products || [];
        if (!employerId) return;

        dispatch(updateEmployerProducts(employerId, {
            products: [...employerProducts, 'PAYROLL']
        }));
    }
    
    
    
    useEffect(() => {
        if (employerProducts?.status === "success") {
            updateEmployerProductsState(employerProducts?.employer?.products);
            dispatch(resetEmployerProductsAction());
            localStorage.setItem(`completedPayrollOnboarding-${userInfo?.employer?._id}`, "true");
            history.push("/payroll-configuration/pay-schedule");
            setStep(1);
        }
    }, [employerProducts, userInfo])
    
    return (
        <div>
            <Maybe condition={step === 1}>
                <WelcomeScreen
                    step={step}
                    image={<PayDay className="w-full h-full object-contain" />}
                    title="Payroll Simplified"
                    description="End the monthly hassles of making payroll payments. Our solution enables you pay all your employees at the click of a button."
                    submitText="Next"
                    handleNext={() => setStep(2)}
                />
            </Maybe>
            
            <Maybe condition={step === 2}>
                <WelcomeScreen
                    step={step}
                    image={<PayrollDocument className="w-full h-full object-contain" />}
                    title="Complex Math Simplified"
                    description="Automatically compute net salaries and other deductions such as tax, pension, housing and insurance, and comply with tax requirements."
                    submitText="Next"
                    handleNext={() => setStep(3)}
                />
            </Maybe>
            
            <Maybe condition={step === 3}>
                <WelcomeScreen
                    step={step}
                    image={<LineChart className="w-full h-full object-contain" />}
                    title="People Operations Simplified"
                    description="Create custom groups, pay structures, schedules and more for full control of employee benefits and welfare."
                    submitText="Get Started"
                    handleNext={handleCompletePayrollOnboarding}
                    loading={employerProductsLoading}
                />
            </Maybe>
        </div>
    )
}
